<template>
  <el-row
    v-loading.fullscreen.lock="fullScreenLoading"
  >
    <el-col
      :sm="{span:14, offset: 5}"
      :md="{span:12, offset: 6}"
      :lg="{span:12, offset: 6}"
    >
      <el-header
        height="140px"
        class="app-header pt-50 pb-30"
      >
        <Menu />
        <h3 class="app-header-title mt-0">
          {{ $t('organization.edit-organization') }}
        </h3>
      </el-header>
      <el-main>
        <el-form
          :model="organization"
          :rules="getFormRules"
          ref="editOrganizationForm"
          label-position="top"
        >
          <el-form-item
            :label="$t('organization.name')"
            prop="name"
          >
            <el-input v-model="organization.name" />
          </el-form-item>
          <el-form-item
            :label="$t('organization.resp-contact')"
            prop="representativeId"
          >
            <el-select
              v-model="organization.representativeId"
              filterable
            >
              <el-option
                v-for="user in userList"
                :key="user.sub"
                :label="`${user.firstName} ${user.surName}(${user.phone})`"
                :value="user.sub"
              />
            </el-select>
          </el-form-item>
          <el-form-item
            :label="$t('organization.type-it-system.title')"
            prop="typeOfITSystem"
          >
            <el-select
              v-model="organization.typeOfITSystem"
            >
              <el-option
                :label="$t('organization.type-it-system.options.plastic-card')"
                value="Plastic card"
              />
              <el-option
                :label="$t('organization.type-it-system.options.stickers')"
                value="Stickers"
              />
              <el-option
                :label="$t('organization.type-it-system.options.generated')"
                value="Generated"
              />
            </el-select>
          </el-form-item>
          <el-form-item
            :label="$t('organization.type-sharing-tips.title')"
            prop="sharingType"
          >
            <el-select
              v-model="organization.sharingType"
            >
              <el-option
                :label="$t('organization.type-sharing-tips.options.individual')"
                value="Individual"
              />
              <el-option
                :label="$t('organization.type-sharing-tips.options.pooling')"
                value="Pooling"
              />
            </el-select>
          </el-form-item>
          <el-form-item
            v-if="isAdmin && organization.sharingType === 'Pooling'"
            :label="$t('re-conciliation.choose-team.strategy')"
            prop="poolingStrategy"
          >
            <el-select
              v-model="organization.poolingStrategy"
            >
              <el-option
                :label="$t('re-conciliation.choose-team.complex')"
                value="complex"
              />
              <el-option
                :label="$t('re-conciliation.choose-team.simple')"
                value="simple"
              />
              <el-option
                :label="$t('re-conciliation.choose-team.auto-pooling')"
                value="auto-pooling"
              />
            </el-select>
          </el-form-item>
          <el-form-item
            :label="$t('organization.industry.title')"
            prop="industry"
            v-if="isAdmin"
          >
            <el-select
              v-model="organization.industry"
            >
              <el-option
                :label="$t('organization.industry.options.barber')"
                value="Friseur"
              />
              <el-option
                :label="$t('organization.industry.options.hotel')"
                value="Hotel"
              />
              <el-option
                :label="$t('organization.industry.options.restaurant')"
                value="Restaurant"
              />
              <el-option
                :label="$t('organization.industry.options.charity')"
                value="Charity"
              />
              <el-option
                :label="$t('organization.industry.options.other')"
                value="Other"
              />
            </el-select>
          </el-form-item>
          <el-form-item
            :label="$t('organization.platform-fee')"
            prop="fee"
          >
            <el-input-number
              :disabled="!isAdmin"
              :controls="false"
              v-model="organization.fee"
              :min="0"
              :max="100"
              :step="0.1"
              :precision="2"
              @change="changeFee"
            />
          </el-form-item>
          <el-form-item
            :label="$t('organization.trial-date')"
            prop="trialDate"
          >
            <el-date-picker
              :disabled="!isAdmin"
              v-model="organization.trialPeriodEnd"
              type="date"
              format="yyyy-MM-dd"
              value-format="yyyy-MM-dd"
            />
          </el-form-item>
          <div
            v-if="isAdmin"
            class="input-group"
          >
            <h2 class="input-group-title">
              {{ $t('organization.pricingPayerPaysFee.title') }}
            </h2>
            <el-form-item
              :label="$t('organization.pricingPayerPaysFee.fixed')"
              prop="fixedFee"
              class="input-group-item"
            >
              <el-input-number
                v-model="organization.clientPaysFeePricing.fixedFee"
                :controls="false"
              />
            </el-form-item>
            <el-form-item
              :label="$t('organization.pricingPayerPaysFee.threshold')"
              prop="threshold"
              class="input-group-item"
            >
              <el-input-number
                v-model="organization.clientPaysFeePricing.threshold"
                :controls="false"
              />
            </el-form-item>
            <el-form-item
              :label="$t('organization.pricingPayerPaysFee.percentage')"
              prop="percentageFeeAboveThreshold"
              class="input-group-item"
            >
              <el-input-number
                v-model="organization.clientPaysFeePricing.percentageFeeAboveThreshold"
                :controls="false"
              />
            </el-form-item>
          </div>
          <div
            v-if="isAdmin"
            class="input-group"
          >
            <h2 class="input-group-title">
              {{ $t('organization.pricingRecipientPaysFee.title') }}
            </h2>
            <el-form-item
              :label="$t('organization.pricingRecipientPaysFee.fixed')"
              prop="fixedFee"
              class="input-group-item"
            >
              <el-input-number
                v-model="organization.recipientPaysFeePricing.fixedFee"
                :controls="false"
              />
            </el-form-item>
            <el-form-item
              :label="$t('organization.pricingRecipientPaysFee.threshold')"
              prop="threshold"
              class="input-group-item"
            >
              <el-input-number
                v-model="organization.recipientPaysFeePricing.threshold"
                :controls="false"
              />
            </el-form-item>
            <el-form-item
              :label="$t('organization.pricingRecipientPaysFee.percentage')"
              prop="percentageFeeAboveThreshold"
              class="input-group-item"
            >
              <el-input-number
                v-model="organization.recipientPaysFeePricing.percentageFeeAboveThreshold"
                :controls="false"
              />
            </el-form-item>
          </div>
          <div
            v-if="isAdmin"
            class="input-group"
          >
            <h2 class="input-group-title">
              {{ $t('organization.pricingPayout.title') }}
            </h2>
            <el-form-item
              :label="$t('organization.pricingPayout.threshold')"
              prop="thresholdForFreePayout"
              class="input-group-item"
            >
              <el-input-number
                v-model="organization.payoutPricing.thresholdForFreePayout"
                :controls="false"
              />
            </el-form-item>
            <el-form-item
              :label="$t('organization.pricingPayout.percentage')"
              prop="feeForPayoutAboveThreshold"
              class="input-group-item"
            >
              <el-input-number
                v-model="organization.payoutPricing.feeForPayoutAboveThreshold"
                :controls="false"
              />
            </el-form-item>
          </div>
          <div
            class="input-group"
          >
            <h2 class="input-group-title">
              {{ $t('organization.post-address') }}
            </h2>
            <el-form-item
              :label="$t('organization.business-name')"
              prop="legalBusinessName"
              class="input-group-item"
            >
              <el-input v-model="organization.legalBusinessName" />
            </el-form-item>
            <el-form-item
              :label="$t('profile.address')"
              prop="addressStreet"
              class="input-group-item"
            >
              <el-input v-model="organization.addressStreet" />
            </el-form-item>
            <el-form-item
              :label="$t('profile.postal_code')"
              prop="addressZip"
              class="input-group-item"
            >
              <div class="el-input">
                <the-mask
                  class="el-input__inner"
                  :mask="'#####'"
                  v-model="organization.addressZip"
                />
              </div>
            </el-form-item>
            <el-form-item
              :label="$t('profile.city')"
              prop="addressCity"
              class="input-group-item"
            >
              <el-input v-model="organization.addressCity" />
            </el-form-item>
            <el-form-item
              :label="$t('profile.country')"
              prop="addressCountry"
              class="input-group-item"
            >
              <el-select
                v-model="organization.addressCountry"
                filterable
                :popper-append-to-body="false"
              >
                <el-option
                  v-for="(country, code) in countries"
                  :key="code"
                  :label="country"
                  :value="code"
                />
              </el-select>
            </el-form-item>
          </div>
          <el-form-item
            :label="$t('organization.send-payment-email')"
            prop="sendPaymentEmail"
          >
            <el-radio-group v-model="organization.sendEmailDisabled">
              <el-radio
                :label="false"
              >
                {{ $t('yes') }}
              </el-radio>
              <el-radio
                :label="true"
              >
                {{ $t('no') }}
              </el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item
            :label="$t('organization.primary-color')"
            prop="schemaPrimaryColor"
          >
            <el-color-picker
              v-model="organization.schemaPrimaryColor"
            />
          </el-form-item>
          <!-- <el-form-item
            :label="$t('organization.feedback-enabled')"
            prop="feedbackEnabled"
          >
            <el-switch v-model="organization.feedbackEnabled" />
          </el-form-item> -->
          <el-form-item
            :label="$t('organization.google-review.enabled')"
            prop="googleReviewEnabled"
          >
            <el-switch v-model="organization.googlereviewEnabled" />
          </el-form-item>
          <el-form-item
            :label="$t('organization.google-review.url')"
            v-if="organization.googlereviewEnabled"
            prop="googleReviewUrl"
          >
            <el-input
              v-model="organization.googlereviewUrl"
            />
          </el-form-item>
          <el-form-item
            :label="$t('organization.tripadvisor-review.enabled')"
            prop="tripadvisorReviewEnabled"
          >
            <el-switch v-model="organization.tripadvisorEnabled" />
          </el-form-item>
          <el-form-item
            :label="$t('organization.tripadvisor-review.url')"
            v-if="organization.tripadvisorEnabled"
            prop="tripadvisorReviewUrl"
          >
            <el-input
              v-model="organization.tripadvisorUrl"
            />
          </el-form-item>
          <el-form-item
            v-if="isAdmin"
            :label="$t('teams.group-qr-code')"
            prop="groupQrCode"
          >
            <el-input-number
              :disabled="!isAdmin"
              :controls="false"
              v-model="organization.groupQrCode"
              :precision="0"
            />
          </el-form-item>
          <el-form-item
            v-if="isAdmin"
            :label="$t('organization.qr-code-ranges')"
            prop="qrCodeRanges"
          >
            <qr-code-ranges-repeater
              :is-admin="isAdmin"
              prop="qrCodeRanges"
              :fields="organization.qrCodeRanges"
            />
          </el-form-item>
          <el-form-item
            v-show="isAdmin"
            :label="$t('opera.payment-methods')"
            prop="enabledPaymentMethods"
          >
            <template v-for="(method, key) in availablePaymentMethods">
              <el-checkbox
                :key="key"
                :value="key"
                v-model="organization.enabledPaymentMethods"
                :label="method"
              />
            </template>
          </el-form-item>
          <el-form-item
            v-if="isAdmin"
            :label="$t('team.paypal-threshold', {threshold: organization.payPalConfig.threshold / 100})"
            prop="payPalConfig"
          >
            <el-radio-group v-model="organization.payPalConfig.thresholdEnabled">
              <el-radio
                :label="true"
              >
                {{ $t('yes') }}
              </el-radio>
              <el-radio
                :label="false"
              >
                {{ $t('no') }}
              </el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item
            v-show="isAdmin && !organization.waiterPaysFee"
            :label="$t('fee.tippie-pays')"
            prop="tippiePays"
          >
            <el-radio-group v-model="organization.tippiePaysFee">
              <el-radio
                :label="true"
              >
                {{ $t('yes') }}
              </el-radio>
              <el-radio
                :label="false"
              >
                {{ $t('no') }}
              </el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item
            v-show="isAdmin"
            :label="$t('fee.organization-pays')"
            prop="waiterPaysFee"
          >
            <el-radio-group v-model="organization.waiterPaysFee">
              <el-radio
                :label="true"
              >
                {{ $t('yes') }}
              </el-radio>
              <el-radio
                :label="false"
              >
                {{ $t('no') }}
              </el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item
            v-show="isAdmin"
            :label="$t('presentation.toggle')"
            prop="presentationMode"
          >
            <el-radio-group v-model="organization.presentationMode">
              <el-radio
                :label="true"
              >
                {{ $t('yes') }}
              </el-radio>
              <el-radio
                :label="false"
              >
                {{ $t('no') }}
              </el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item
            v-if="isAdmin"
            :label="$t('organization.hide-paypal')"
            prop="hidePaypal"
          >
            <el-radio-group v-model="organization.hidePaypal">
              <el-radio
                :label="true"
              >
                {{ $t('yes') }}
              </el-radio>
              <el-radio
                :label="false"
              >
                {{ $t('no') }}
              </el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item
            v-show="isAdmin"
            :label="$t('new-design.payments')"
            prop="newDesignPayment"
          >
            <el-radio-group v-model="organization.newDesignPayment">
              <el-radio
                :label="true"
              >
                {{ $t('yes') }}
              </el-radio>
              <el-radio
                :label="false"
              >
                {{ $t('no') }}
              </el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item
            v-show="isAdmin"
            :label="$t('new-design.admin')"
            prop="newDesignAdmin"
          >
            <el-radio-group v-model="organization.newDesignAdmin">
              <el-radio
                :label="true"
              >
                {{ $t('yes') }}
              </el-radio>
              <el-radio
                :label="false"
              >
                {{ $t('no') }}
              </el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item class="mt-20">
            <el-row>
              <el-col :span="11">
                <router-link
                  :to="{ name: 'Organization', params: { id: organization.id } }"
                  custom
                  v-slot="{ navigate }"
                >
                  <el-button
                    class="cancel-button"
                    @click="navigate"
                  >
                    {{ $t('form.cancel') }}
                  </el-button>
                </router-link>
              </el-col>
              <el-col
                :offset="2"
                :span="11"
              >
                <el-button
                  class="primary-button"
                  type="primary"
                  @click="submitForm('editOrganizationForm')"
                >
                  {{ $t('form.save') }}
                </el-button>
              </el-col>
            </el-row>
          </el-form-item>
        </el-form>
      </el-main>
    </el-col>
  </el-row>
</template>

<script>
import Menu from '@/views/Menu/Menu';
import authHelpers from '@/plugins/auth-helpers';
import QrCodeRangesRepeater from '../Components/Repeaters/QrCodeRangesRepeater';
import countriesList from '../../assets/countries.json';

export default {
  name: 'EditOrganization',
  components: {
    Menu,
    QrCodeRangesRepeater
  },
  data: function () {
    return {
      availablePaymentMethods: {
        'ApplePay': 'Apple Pay',
        'GooglePay': 'Google Pay',
        'CreditCard': 'Credit Card',
        'PayPal': 'PayPal',
        'Klarna': 'Klarna',
        'ApplePayNoda': 'Noda Apple Pay',
        'CreditCardNoda': 'Noda Credit Card',
        'OpenBankNoda': 'Noda Open Bank',
      },
      methodsMapping: {
        'Apple Pay': 'ApplePay',
        'Google Pay': 'GooglePay',
        'Credit Card': 'CreditCard',
        'PayPal': 'PayPal',
        'Klarna': 'Klarna',
        'Noda Apple Pay': 'ApplePayNoda',
        'Noda Credit Card': 'CreditCardNoda',
        'Noda Open Bank': 'OpenBankNoda',
      },
      isAdmin: false,
      fullScreenLoading: true,
      organization: {
        payPalConfig: {
          thresholdEnabled: false,
          threshold: 500,
        },
        enabledPaymentMethods: [],
        // feedbackEnabled: false,
        sendEmailDisabled: false,
        sharingType: '',
        poolingStrategy: 'complex',
        tippiePaysFee: false,
        waiterPaysFee: false,
        googlereviewEnabled: false,
        googlereviewUrl: '',
        tripadvisorEnabled: false,
        presentationMode: false,
        tripadvisorUrl: '',
        legalBusinessName: '',
        addressCountry: '',
        addressCity: '',
        addressStreet: '',
        addressZip: '',
        industry: null,
        hidePaypal: false,
        newDesignPayment: false,
        newDesignAdmin: false,
        clientPaysFeePricing: {
          fixedFee: undefined,
          threshold: undefined,
          percentageFeeAboveThreshold: undefined,
        },
        recipientPaysFeePricing: {
          fixedFee: undefined,
          threshold: undefined,
          percentageFeeAboveThreshold: undefined
        },
        payoutPricing: {
          thresholdForFreePayout: undefined,
          feeForPayoutAboveThreshold: undefined
        }
      },
      userList: []
    };
  },
  created () {
    let promises = [];
    promises.push(new Promise((resolve) => {
      authHelpers.isCurrentUserAdmin().then((response) => {
        this.isAdmin = response;
        resolve();
      });
    }));
    promises.push(new Promise((resolve) => {
      this.$adminApi.get('api/v1/entities/' + this.$route.params.id).then((response) => {
        this.organization = {
          ...this.organization,
          ...response.data
        };

        if (!this.organization.enabledPaymentMethods) {
          this.organization.enabledPaymentMethods = [];
        }


        if (this.organization.payPalConfig == null) {
          this.organization.payPalConfig = {
            thresholdEnabled: false,
            threshold: 500,
          };
        }

        if (!this.organization.presentationMode) {
          this.organization.presentationMode = false;
        }
        if (!this.organization.qrCodeRanges) {
          this.organization.qrCodeRanges = [
            {
              qrCodeStart: undefined,
              qrRangeSize: undefined
            }
          ];
        }

        if (!this.organization.clientPaysFeePricing) {
          this.organization.clientPaysFeePricing = {
            fixedFee: undefined,
            threshold: undefined,
            percentageFeeAboveThreshold: undefined
          };
        }

        if (!this.organization.recipientPaysFeePricing) {
          this.organization.recipientPaysFeePricing = {
            fixedFee: undefined,
            threshold: undefined,
            percentageFeeAboveThreshold: undefined
          };
        }

        if (!this.organization.payoutPricing) {
          this.organization.payoutPricing = {
            thresholdForFreePayout: undefined,
            feeForPayoutAboveThreshold: undefined
          };
        }

        this.organization.enabledPaymentMethods = this.organization.enabledPaymentMethods.map((method) => {
          return this.availablePaymentMethods[method];
        });

        this.organization.fee = this.roundToDecimals(parseInt(response.data.feePercentage) / 100);
        this.organization.id = this.$route.params.id;
        resolve();
      });
    }));
    promises.push(new Promise((resolve) => {
      this.getUsersFromGroup('business', this.userList);
      resolve();
    }));
    promises.push(new Promise((resolve) => {
      this.getUsersFromGroup('admin', this.userList);
      resolve();
    }));
    Promise.all(promises).then(() => {
      this.fullScreenLoading = false;
    })
      .catch(() => {
        this.fullScreenLoading = false;
      });
  },
  computed: {
    getFormRules: function () {
      const qrCodeRangesValidator = (rule, value, callback) => {
        let qrCodeStartValid = true;
        let qrRangeSizeValid = true;
        value.map((el) => {
          if (!el.qrCodeStart) {
            qrCodeStartValid = true;
          }
          if (!el.qrRangeSize) {
            qrRangeSizeValid = false;
          }
        });
        if (!qrCodeStartValid) {
          callback(new Error(this.$t('form.validation.all-items-populated')));
        }
        if (!qrRangeSizeValid) {
          callback(new Error(this.$t('form.validation.all-items-populated')));
        }
        callback();
      };
      return {
        name: [
          {
            required: true,
            message: this.$t('form.validation.required', { field: this.$t('organization.name') }),
            trigger: 'blur'
          }
        ],
        respContact: [
          {
            required: true,
            message: this.$t('form.validation.required', { field: this.$t('organization.resp-contact') }),
            trigger: 'change'
          }
        ],
        itSystem: [
          {
            required: true,
            message: this.$t('form.validation.required', { field: this.$t('organization.type-it-system.title') }),
            trigger: 'blur'
          }
        ],
        industry: [
          {
            required: true,
            message: this.$t('form.validation.required', { field: this.$t('organization.industry.title') }),
            trigger: 'change'
          }
        ],
        sharingType: [
          {
            required: true,
            message: this.$t('form.validation.required', { field: this.$t('organization.type-sharing-tips.title') }),
            trigger: 'change'
          }
        ],
        fee: [
          {
            required: true,
            message: this.$t('form.validation.required', { field: this.$t('organization.platform-fee') }),
            trigger: 'blur'
          }
        ],
        qrCodeRanges: [
          { validator: qrCodeRangesValidator }
        ]
      };
    },
    countries: () => {
      return countriesList;
    }
  },
  watch: {
    'organization.waiterPaysFee' (newVal) {
      if (newVal) {
        this.organization.tippiePaysFee = false;
      }
    }
  },
  methods: {
    submitForm (formName) {
      this.$refs[formName].validate((valid) => {
        if (valid) {
          this.updateOrganization();
        } else {
          return false;
        }
      });
    },
    updateOrganization () {
      this.fullscreenLoading = true;

      let organizationObject = {
        ...this.organization
      };

      if(Object.values(organizationObject.payoutPricing).some((val) => val == null)) {
        organizationObject.payoutPricing = null
      }
     
      if(Object.values(organizationObject.clientPaysFeePricing).some((val) => val == null)) {
        organizationObject.clientPaysFeePricing = null
      }

      if(Object.values(organizationObject.recipientPaysFeePricing).some((val) => val == null)) {
        organizationObject.recipientPaysFeePricing = null
      }
 
      organizationObject.enabledPaymentMethods = organizationObject.enabledPaymentMethods.map((method) => {
        return this.methodsMapping[method];
      });
      this.$adminApi.put('/api/v1/entities', organizationObject).then((response) => {
        this.fullscreenLoading = false;
        if (response.data.status === 'Success') {
          this.$message.success(this.$t('form.item-changed', { item: this.$t('organization.title') }));
        } else {
          this.$message.warning(response.data.status);
        }
        this.$router.push({ name: 'Organization', params: this.$route.params });
      }).catch(() => {
        this.fullscreenLoading = false;
        this.$message.error(this.$t('form.item-not-changed', { item: this.$t('organization.title') }));
      });
    },
    changeFee (val) {
      this.organization.feePercentage = parseFloat(val) * 100;
    }
  }
};
</script>
<style lang="scss" scoped>

</style>
